import type React from 'react'
import type { StatusBadgeTone } from 'atoms/Label/Templates'
import type { ILabelGroupTemplate3MaxLabels6Props } from 'blocks/LabelGroup/Templates'
import type { IMultiColumnsItemProps } from '../../MultiColumnsItem'

import { LabelsOrder } from 'atoms/Label/Label.h'
import {
  LabelAgeRating,
  LabelContentBadge,
  LabelPurchaseBadge,
  LabelStatusBadge,
  LabelVerticalVideo,
} from 'atoms/Label/Templates'
import { TextBox } from 'atoms/TextBox'
import { TextBoxMaxLine2Font4xl } from 'atoms/TextBox/Templates'
import { Colors, TextAligning, TextBoxTypes } from 'atoms/TextBox/TextBox.h'
import { LabelGroupTemplate3MaxLabels6 } from 'blocks/LabelGroup/Templates'
import { MultiColumnsRowGap } from '../../MultiColumns.h'
import { MultiColumnsItem } from '../../MultiColumnsItem'
import {
  ContentBadgesGroupSC,
  TitleSubtitleSC,
} from './MCItemTitleWithLabels.styled'

export interface IMCItemTitleWithLabels extends Omit<
  IMultiColumnsItemProps,
  'children'
> {
  children?: React.ReactNode
  labels?: ILabelGroupTemplate3MaxLabels6Props['list']
  /** WBS content labels, already translated, rendered as badges above the title. */
  contentLabels?: string[]
  /** Live event state pill above the title; empty once the event has finished. */
  statusBadge?: string
  statusBadgeTone?: StatusBadgeTone
  /** Event details: Live / Upcoming first, then paid, then age rating. */
  statusBadgeFirst?: boolean
  /** Age rating badge above the title (PIN-locked VOD/shows; event details). */
  ageRatingLabel?: string
  /** Content that has to be bought — the crown badge above the title. */
  paid?: boolean
  /** Rented content — remaining availability, such as `30 days`. */
  rented?: boolean
  purchaseTitle?: string
  quality?: string
  dolbyAtmos?: boolean
  verticalVideo?: boolean
  title: string
  /** Overrides the 2-line title clamp — 4 for the details `banner-description`. */
  titleMaxLines?: number
  /** Access-state line under the title: the PIN-lock hint or geoblock message. */
  subtitle?: string
  dataTestId?: string
}

export const MCItemTitleWithLabels = ({
  labels,
  contentLabels,
  statusBadge,
  statusBadgeTone,
  statusBadgeFirst = false,
  ageRatingLabel,
  paid,
  rented,
  purchaseTitle,
  title,
  titleMaxLines,
  subtitle,
  rowGap = MultiColumnsRowGap.INDENT_20,
  children: _unused,
  dataTestId,
  quality,
  dolbyAtmos,
  verticalVideo,
  ...rest
}: IMCItemTitleWithLabels) => {
  const purchaseBadge = Boolean(paid) || Boolean(rented && purchaseTitle)
  const showAgeRating = Boolean(ageRatingLabel)
  const showTopLabels =
    purchaseBadge ||
    statusBadge ||
    Boolean(contentLabels?.length) ||
    verticalVideo ||
    showAgeRating

  return (
    <MultiColumnsItem rowGap={rowGap} {...rest}>
      {showTopLabels ? (
        <ContentBadgesGroupSC>
          {statusBadgeFirst && statusBadge ? (
            <LabelStatusBadge title={statusBadge} tone={statusBadgeTone} />
          ) : null}
          {contentLabels?.map((contentLabel) => (
            <LabelContentBadge key={contentLabel} title={contentLabel} />
          ))}
          {purchaseBadge ? (
            <LabelPurchaseBadge title={rented ? purchaseTitle : ''} />
          ) : null}
          {verticalVideo ? (
            <LabelVerticalVideo
              data-testid='test-vertical-video-label'
              // Override the atom default (ORDER_3) so visual LTR follows DOM order.
              order={LabelsOrder.ORDER_1}
            />
          ) : null}
          {!statusBadgeFirst && statusBadge ? (
            <LabelStatusBadge title={statusBadge} tone={statusBadgeTone} />
          ) : null}
          {showAgeRating ? (
            <LabelAgeRating
              data-testid='age-rating-label'
              title={ageRatingLabel ?? ''}
            />
          ) : null}
        </ContentBadgesGroupSC>
      ) : null}
      {title && (
        <TextBoxMaxLine2Font4xl
          title={title}
          dataTestId={dataTestId}
          maxLines={titleMaxLines}
        />
      )}
      {subtitle ? (
        <TitleSubtitleSC>
          <TextBox
            title={subtitle}
            color={Colors.TEXT_ICON_SECONDARY}
            font={TextBoxTypes.FontXs}
            alignment={TextAligning.Left}
            maxLines={2}
          />
        </TitleSubtitleSC>
      ) : null}
      {(labels || quality || dolbyAtmos) && (
        <LabelGroupTemplate3MaxLabels6
          list={labels || []}
          quality={quality}
          dolbyAtmos={dolbyAtmos}
        />
      )}
    </MultiColumnsItem>
  )
}
